import { useCallback } from "react";
import { ListItemText, MenuItem, MenuList } from "@mui/material";
import cx from "classnames";
import { HISTORY_DROPDOWN_EMPTY_MESSAGE } from "../../AiToolboxSection.constants";
import type { ChatSession } from "../../AiToolboxSection.types";
import { useInlineEdit } from "./ChatHistory.hooks";
import { ChatHistorySessionMenuItem } from "./components/ChatHistorySessionMenuItem/ChatHistorySessionMenuItem";

export interface ChatHistoryProps {
  sessions: ChatSession[];
  activeSessionId: string | null;
  onSwitchSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, newTitle: string) => void;
  className?: string;
}

const ChatHistory = ({
  sessions,
  activeSessionId,
  onSwitchSession,
  onDeleteSession,
  onRenameSession,
  className,
}: ChatHistoryProps) => {
  const inlineEdit = useInlineEdit({ onSave: onRenameSession });

  const handleStartEdit = useCallback(
    (sessionId: string, currentTitle: string) => {
      inlineEdit.startEdit(sessionId, currentTitle);
    },
    [inlineEdit]
  );

  const handleDeleteClick = useCallback(
    (sessionId: string, sessionTitle: string) => {
      const confirmed = window.confirm(
        `Delete chat "${sessionTitle}"?\n\nThis action cannot be undone.`
      );

      if (confirmed) {
        onDeleteSession(sessionId);
      }
    },
    [onDeleteSession]
  );

  const isEmpty = sessions.length === 0;

  return (
    <MenuList disablePadding className={cx("w-full", className)}>
      {isEmpty ? (
        <MenuItem disabled>
          <ListItemText
            primary={HISTORY_DROPDOWN_EMPTY_MESSAGE}
            slotProps={{
              primary: { className: "text-xs text-text-secondary" },
            }}
          />
        </MenuItem>
      ) : (
        sessions.map((session, index) => (
          <ChatHistorySessionMenuItem
            key={session.id}
            session={session}
            activeSessionId={activeSessionId}
            isLastItem={index === sessions.length - 1}
            inlineEdit={inlineEdit}
            onSelectSession={onSwitchSession}
            onStartEdit={handleStartEdit}
            onDeleteSession={handleDeleteClick}
          />
        ))
      )}
    </MenuList>
  );
};

export default ChatHistory;
